/**
 * @spec StudentAttendance Component
 *
 * Purpose: Display and manage a student's attendance history for the current school year
 *
 * Requirements:
 * 1. Display student's attendance records in a table format
 * 2. Show the following columns:
 *    - Date (MM/DD format)
 *    - In (signed in time or "—" if not signed in)
 *    - Out (signed out time or "—" if not signed out)
 *    - Absent? (show absence reason or "—" if present)
 *    - Granted Hours (show granted_hours value or "—" if none)
 * 3. Use NEW schema fields from attendance_records table:
 *    - granted_hours (not hours_credited)
 *    - granted_reason (not absence_reason)
 *    - granted_details (not absence_reason_other_description)
 *    - hours_earned
 *    - day_earned
 * 4. Display weekends and holidays as special rows
 * 5. Show "—" for absence column when student is present (has sign in/out times)
 * 6. Show granted reason when student was absent or granted extra hours
 * 7. Display "—" when granted_hours is null/undefined
 * 8. Allow admin users to edit attendance records
 */

/**
 * @spec StudentAttendance Component
 *
 * Purpose: Display and manage a student's attendance history for the current school year
 *
 * Requirements:
 * 1. Display student's attendance records in a table format
 * 2. Show the following columns:
 *    - Date (MM/DD format)
 *    - In (signed in time or "—" if not signed in)
 *    - Out (signed out time or "—" if not signed out)
 *    - Absent? (show absence reason or "—" if present)
 *    - Granted Hours (show granted_hours value or "—" if null/undefined/0)
 * 3. Use NEW schema fields from attendance_records table:
 *    - granted_hours (not hours_credited)
 *    - granted_reason (not absence_reason)
 *    - granted_details (not absence_reason_other_description)
 *    - hours_earned
 *    - day_earned
 * 4. Display weekends and holidays as special rows
 * 5. Show "—" for absence column when student is present (has sign in/out times)
 * 6. Show granted reason when student was absent or granted extra hours
 * 7. Display "—" (not NaN) when granted_hours is null/undefined/0
 * 8. Allow admin users to edit attendance records
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Modal, Box, TextField, Button, MenuItem, FormControl, IconButton,
    InputLabel, Select, Typography, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormHelperText,
    Checkbox, Link as MuiLink, Switch, FormControlLabel
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { format, parse } from 'date-fns';
import { CircularProgress } from '@mui/material';
import { formatTime24To12, formatDate, authenticatedFetch, roundToNearestTenth, getAbsenceDisplay } from '../utils/utils';
import AttendanceRecordModal from '../components/AttendanceRecordModal';

const StudentAttendance = ({ isAdmin }) => {
    const { studentId, date } = useParams();
    const [student, setStudent] = useState(null);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [calendar, setCalendar] = useState(null);
    const [selectedDate, setSelectedDate] = useState(date ? parse(date, 'yyyy-MM-dd', new Date()) : new Date());
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        date: '',
        signed_in_at: '',
        signed_out_at: '',
        granted_hours: 0,
        granted_reason: null,
        granted_details: null,
        is_absent: false,
        is_personal_day: false,
        grant_hours: false
    });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [editingMultiple, setEditingMultiple] = useState(false);
    const [lastCheckedIndex, setLastCheckedIndex] = useState(null);

    // Move isSelected definition here
    const isSelected = useCallback((record) => {
        return selectedRecords.some(r => r.date === record.date);
    }, [selectedRecords]);

    useEffect(() => {
        fetchStudentData();
    }, [studentId, selectedDate]);

    const fetchStudentData = async () => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/students/${studentId}/attendance/${selectedDate.toISOString()}`);
            if (!response.ok) throw new Error('Failed to fetch student data and attendance records');
            const data = await response.json();
            setStudent(data.student);
            setAttendanceRecords(data.attendance);
            setCalendar(data.calendar);
        } catch (error) {
            setAlert({ message: error.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = useCallback((record, index, event) => {
        event.stopPropagation(); // Prevent row click event

        setSelectedRecords(prevSelected => {
            let newSelected = [...prevSelected];
            
            if (event.nativeEvent.shiftKey && lastCheckedIndex !== null) {
                const start = Math.min(lastCheckedIndex, index);
                const end = Math.max(lastCheckedIndex, index);

                // Determine if we're selecting or deselecting
                const isSelecting = !isSelected(record);
                
                for (let i = start; i <= end; i++) {
                    const currentRecord = attendanceRecords[i];
                    if (!currentRecord.holiday) {
                        const alreadySelected = newSelected.some(r => r.date === currentRecord.date);
                        if (isSelecting && !alreadySelected) {
                            newSelected.push(currentRecord);
                        } else if (!isSelecting && alreadySelected) {
                            newSelected = newSelected.filter(r => r.date !== currentRecord.date);
                        }
                    }
                }
            } else {
                const recordIndex = newSelected.findIndex(r => r.date === record.date);
                if (recordIndex !== -1) {
                    newSelected = newSelected.filter(r => r.date !== record.date);
                } else {
                    newSelected.push(record);
                }
            }
            
            console.log('New selected records:', newSelected.map(r => r.date));
            return newSelected;
        });

        setLastCheckedIndex(index);
    }, [lastCheckedIndex, attendanceRecords, isSelected]);

    const handleRowClick = (record) => {
        if (selectedRecords.length > 0) {
            handleEditMultiple();
        } else {
            // Determine UI state based on existing attendance data
            let isAbsent = !record.signed_in_at && !record.signed_out_at;
            let isPersonalDay = true;
            let grantHours = false;

            if (isAbsent) {
                // For absent records, check if personal day or granted absence
                isPersonalDay = !record.granted_hours || record.granted_hours === 0;
                grantHours = Boolean(record.granted_hours && record.granted_hours > 0);
            } else {
                // For present records, check if hours were granted
                grantHours = Boolean(record.granted_hours && record.granted_hours > 0);
            }

            setFormData({
                ...record,
                student: student, // Include student for default_granted_hours
                date: formatDate(new Date(record.date)),
                signed_in_at: record.signed_in_at || '',
                signed_out_at: record.signed_out_at || '',
                // UI state fields
                is_absent: isAbsent,
                is_personal_day: isPersonalDay,
                grant_hours: grantHours,
                granted_hours: record.granted_hours || 0,
                granted_reason: record.granted_reason || '',
                granted_details: record.granted_details || '',
                hours_earned: record.hours_earned || 0,
                day_earned: record.day_earned || false
            });
            setFormErrors({});
            setEditingMultiple(false);
            setModalOpen(true);
        }
    };

    const handleEditMultiple = () => {
        setFormData({
            student: student, // Include student for default_granted_hours
            date: '',
            signed_in_at: '',
            signed_out_at: '',
            // UI state fields
            is_absent: false,
            is_personal_day: true,
            grant_hours: false,
            granted_hours: 0,
            granted_reason: '',
            granted_details: '',
            hours_earned: 0,
            day_earned: false
        });
        setFormErrors({});
        setEditingMultiple(true);
        setModalOpen(true);
    };

    const currentTime = () => {
        return new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
    }

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // New toggle handlers for the switches
    const handleAbsentToggle = () => {
        const newIsAbsent = !formData.is_absent;

        if (newIsAbsent) {
            // Switching to absent - clear sign in/out times, default to personal day
            setFormData({
                ...formData,
                is_absent: true,
                is_personal_day: true,
                grant_hours: false,
                signed_in_at: '',
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Switching to present - set default sign in time if needed
            setFormData({
                ...formData,
                is_absent: false,
                is_personal_day: false,
                grant_hours: false,
                signed_in_at: currentTime(),
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const handlePersonalDayToggle = () => {
        const newIsPersonalDay = !formData.is_personal_day;

        if (newIsPersonalDay) {
            // Switching to personal day - clear granted fields
            setFormData({
                ...formData,
                is_personal_day: true,
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Switching to granted absence - use default granted hours from student data
            setFormData({
                ...formData,
                is_personal_day: false,
                granted_hours: student?.default_granted_hours || 5.0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const handleGrantHoursToggle = () => {
        const newGrantHours = !formData.grant_hours;

        if (newGrantHours) {
            // Enable grant hours - use student's default_granted_hours
            setFormData({
                ...formData,
                grant_hours: true,
                granted_hours: student?.default_granted_hours || formData.student?.default_granted_hours || 5.0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Disable grant hours - clear granted fields
            setFormData({
                ...formData,
                grant_hours: false,
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const clearField = (fieldName) => {
        setFormData({ ...formData, [fieldName]: '' });
    };

    const validateFormData = (data = formData) => {
        const errors = {};

        // Present student validation
        if (!data.is_absent) {
            if (!data.signed_in_at) {
                errors.signed_in_at = "Sign in time is required for present students";
            }
            if (data.signed_in_at && data.signed_out_at && data.signed_in_at > data.signed_out_at) {
                errors.signed_out_at = "Sign-out time must be after sign-in time";
            }

            // Grant hours validation for present students
            if (data.grant_hours) {
                if (!data.granted_hours || data.granted_hours <= 0) {
                    errors.granted_hours = "Granted hours must be greater than 0";
                }
                if (!data.granted_reason) {
                    errors.granted_reason = "Reason is required when granting hours";
                }
            }
        } else {
            // Absent student validation
            if (data.grant_hours) {
                // Granted absence validation (when grant_hours is enabled)
                if (!data.granted_hours || data.granted_hours <= 0) {
                    errors.granted_hours = "Granted hours is required";
                }
                if (!data.granted_reason) {
                    errors.granted_reason = "Reason is required when granting hours";
                }
            }
        }

        // General validation
        if (data.granted_hours && (data.granted_hours < 0 || data.granted_hours > 24)) {
            errors.granted_hours = "Granted hours must be between 0 and 24";
        }

        return errors;
    };

    // Backward compatibility wrapper
    const validateForm = () => validateFormData(formData);

    const saveAttendance = async (e) => {
        // Handle both event (from form submit) and formData (from AttendanceRecordModal)
        let dataToValidate = formData;

        if (e && e.preventDefault) {
            e.preventDefault();
        } else if (e && typeof e === 'object' && e.is_absent !== undefined) {
            // e is actually formData from AttendanceRecordModal
            dataToValidate = e;
            setFormData(e);
        }

        const errors = validateFormData(dataToValidate);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setLoading(true);
        try {
            const recordsToUpdate = editingMultiple ? selectedRecords : [dataToValidate];

            for (let record of recordsToUpdate) {
                const updatedRecord = {
                    ...record,
                    ...dataToValidate,
                    date: record.date, // Preserve the original date
                    // Set sign in/out times based on absent state
                    signed_in_at: dataToValidate.is_absent ? null : (dataToValidate.signed_in_at || null),
                    signed_out_at: dataToValidate.is_absent ? null : (dataToValidate.signed_out_at || null),
                    // Set granted fields based on grant_hours state
                    granted_hours: dataToValidate.grant_hours ? dataToValidate.granted_hours : 0,
                    granted_reason: dataToValidate.grant_hours ? dataToValidate.granted_reason : null,
                    granted_details: dataToValidate.grant_hours ? dataToValidate.granted_details : null
                };
                await saveOrUpdateRecord(updatedRecord);
            }

            setModalOpen(false);
            fetchStudentData();
            setAlert({ message: 'Attendance record(s) saved successfully', severity: 'success' });
            setSelectedRecords([]);
            setEditingMultiple(false);
        } catch (error) {
            setAlert({ message: error.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const saveOrUpdateRecord = async (record) => {
        const url = `/attendance/record/${studentId}/${record.date}`;

        // Only send the fields that belong in the database schema
        const payload = {
            signed_in_at: record.signed_in_at,
            signed_out_at: record.signed_out_at,
            granted_hours: record.granted_hours,
            granted_reason: record.granted_reason,
            granted_details: record.granted_details
        };

        const response = await authenticatedFetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error('Failed to save attendance record');
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const deleteAttendance = async () => {
        setLoading(true);
        try {
            if (selectedRecords.length > 0) {
                // Deleting multiple records
                const existingRecords = selectedRecords.filter(record => record.id);
                for (let record of existingRecords) {
                    await deleteRecord(record.date);
                }
                const deletedCount = existingRecords.length;
                const skippedCount = selectedRecords.length - existingRecords.length;
                let message = `${deletedCount} attendance record(s) deleted successfully`;
                if (skippedCount > 0) {
                    message += `. ${skippedCount} non-existent record(s) skipped.`;
                }
                setAlert({ message, severity: 'success' });
            } else {
                // Deleting single record
                if (formData.id) {
                    await deleteRecord(formData.date);
                    setAlert({ message: 'Attendance record deleted successfully', severity: 'success' });
                } else {
                    setAlert({ message: 'No existing record to delete', severity: 'info' });
                }
            }
            setModalOpen(false);
            fetchStudentData();
            setAlert({ message: 'Attendance record(s) deleted successfully', severity: 'success' });
            setSelectedRecords([]);
            setEditingMultiple(false);
        } catch (error) {
            setAlert({ message: 'Failed to delete attendance record(s)', severity: 'error' });
        } finally {
            setLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    const deleteRecord = async (date) => {
        const response = await authenticatedFetch(`/attendance/record/${studentId}/${date}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete attendance record');
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 2,
    };


    // Helper function to format granted hours display
    const getGrantedHoursDisplay = (record) => {
        if (record.granted_hours !== null && record.granted_hours !== undefined && record.granted_hours !== 0) {
            return roundToNearestTenth(record.granted_hours);
        }
        return '—';
    };


    const renderTableRow = (record, index) => {
        if (record.holiday) {
            return (
                <TableRow key={record.date} sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                        {record.holiday}
                    </TableCell>
                </TableRow>
            );
        }

        return (
            <TableRow
                key={record.date}
                id={`attendance-row-${record.date}`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                onClick={() => handleRowClick(record)}
            >
                <TableCell padding="checkbox">
                    <Checkbox
                        checked={isSelected(record)}
                        onChange={(event) => handleCheckboxChange(record, index, event)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </TableCell>
                <TableCell component="th" scope="row">
                    {record.date.split('-').slice(1, 3).join('/')}
                </TableCell>
                <TableCell>{record.signed_in_at ? formatTime24To12(record.signed_in_at) : '—'}</TableCell>
                <TableCell>{record.signed_out_at ? formatTime24To12(record.signed_out_at) : '—'}</TableCell>
                <TableCell>
                    {record.id
                        ? (() => {
                            const display = getAbsenceDisplay(record, { showEarnedDay: false, isHoliday: !!record.holiday });
                            if (display === "!") {
                                return <span className="error-icon" style={{ color: 'red' }}>!</span>;
                            }
                            return display;
                        })()
                        : 'No Attendance Record'
                    }
                </TableCell>
                <TableCell>{getGrantedHoursDisplay(record)}</TableCell>
            </TableRow>
        );
    };

    const scrollToToday = useCallback(() => {
        const today = new Date().toISOString().split('T')[0];
        const todayElement = document.getElementById(`attendance-row-${today}`);
        if (todayElement) {
            todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, []);

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                    {student && calendar 
                        ? `${student.nickname} Attendance: ${new Date(calendar.first_day).getFullYear()} - ${new Date(calendar.last_day).getFullYear()}` 
                        : 'Loading...'}
                </Typography>
                <MuiLink
                    component="button"
                    variant="body2"
                    onClick={scrollToToday}
                    sx={{ mt: 1, cursor: 'pointer' }}
                >
                    Go to Today
                </MuiLink>
            </Box>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="student attendance table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>In</TableCell>
                            <TableCell>Out</TableCell>
                            <TableCell>Absent?</TableCell>
                            <TableCell>Granted Hours</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {attendanceRecords && attendanceRecords.length > 0 ? (
                            attendanceRecords.map((record, index) => renderTableRow(record, index))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No attendance records found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <AttendanceRecordModal
                open={modalOpen}
                title={editingMultiple
                    ? `Edit ${selectedRecords.length} Attendance Records`
                    : (student ? `Edit Attendance for ${student.nickname}` : 'Edit Attendance Record')}
                showDateField={!editingMultiple}
                showDeleteButton={true}
                initialFormData={formData}
                errors={formErrors}
                onSave={saveAttendance}
                onDelete={handleDeleteClick}
                onClose={() => {
                    setModalOpen(false);
                    setEditingMultiple(false);
                    setSelectedRecords([]);
                }}
                onClearField={clearField}
            />

            <Dialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {selectedRecords.length > 0
                            ? `Are you sure you want to delete these ${selectedRecords.length} attendance records?`
                            : "Are you sure you want to delete this attendance record?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={deleteAttendance} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!alert}
                autoHideDuration={6000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'info'} sx={{ width: '100%' }}>
                    {alert?.message}
                </Alert>
            </Snackbar>

            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 'tooltip',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <CircularProgress />
                </Box>
            )}
        </>
    );
};

export default StudentAttendance;
